<script setup lang="ts">
import { defineEmits, ref } from 'vue'
import { type FormInst, type FormRules, NForm, NFormItem, useMessage } from 'naive-ui'
import { t } from '@/locales'
import { updateClassroom } from '@/api/classroom'

const props = defineProps<{
  show: boolean
  dataSource?: any | null
}>()

const emit = defineEmits(['onClose'])

const message = useMessage()

const showModal = ref(props.show)
const loading = ref(false)

const modelRef = ref({
  name: null,
  description: null,
  class_block: null,
  ...props.dataSource,
})

const formRef = ref<FormInst | null>(null)

const rulesRef: FormRules = {
  name: [
    {
      required: true,
      message: t('validation.required', { name: t('classroom.name') }),
      trigger: ['blur', 'input'],
    },
  ],
}

const closeModal = (reload: boolean) => {
  showModal.value = false
  emit('onClose', reload)
}

const handleCancel = () => {
  closeModal(false)
}

const onUpdateClassroom = async () => {
  if (!modelRef.value?.id) {
    message.error(t('classroom.classroomNotFound'))
    return
  }

  try {
    loading.value = true
    const res = await updateClassroom({
      ...modelRef.value,
    })
    if (res?.data?.success) {
      message.success(t('classroom.updateClassroomSuccess'))
      closeModal(true)
    }
    else {
      message.error(t('classroom.updateClassroomFailed'))
    }
  }
  catch (e: any) {
    message.error(e?.response?.data?.message || t('classroom.updateClassroomFailed'))
  }
  finally {
    loading.value = false
  }
}

const handleUpdateClassroom = () => {
  formRef.value?.validate().then(async () => {
    await onUpdateClassroom()
  }).catch((e: any) => {
    console.log('Validation errors:', e)
  }).finally(() => {})
}
</script>

<template>
  <AppModal
    v-model:show="showModal"
    preset="card"
    transform-origin="center"
    style="width: 520px"
    class="!rounded-3xl"
    content-class="!px-8 !py-0"
    header-class="!p-8"
    footer-class="!p-8 !w-full !pt-3"
    :mask-closable="false"
    @after-leave="handleCancel"
    @esc="handleCancel"
  >
    <template #header>
      <div class="text-center text-2xl font-semibold text-gray-900 dark:text-neutral-100">
        {{ $t('classroom.edit2') }}
      </div>
    </template>

    <div class="w-full">
      <NForm ref="formRef" :model="modelRef" :rules="rulesRef" @keydown.enter.prevent="handleUpdateClassroom">
        <NFormItem :label="t('classroom.name')" path="name">
          <AppInput
            v-model:value="modelRef.name"
            :placeholder="t('classroom.namePlaceholder')"
            size="medium"
          />
        </NFormItem>
      </NForm>
    </div>

    <template #footer>
      <div class="w-full flex gap-3 justify-end items-center">
        <AppButton
          class=" max-w-[120px] !w-full"
          size="medium"
          @click="handleCancel"
        >
          {{ $t('common.cancel') }}
        </AppButton>
        <AppButton
          type="primary"
          class=" max-w-[120px] !w-full"
          size="medium"
          :loading="loading"
          @click="handleUpdateClassroom"
        >
          {{ $t('common.save') }}
        </AppButton>
      </div>
    </template>
  </AppModal>
</template>

<style scoped>

</style>
